import ApiaApi, { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import { TApiaFilter, TApiaFilterOption } from '../../../../../types';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { calculateDate, UserQryFilters } from '../FiltersSetter';
import { arrayOrArray, getLabel } from '../../../../../utils/util';
import store from '../../../../../store';
import { queryActions } from '../../../../../store/querySlice';
import dayjs from 'dayjs';
import { ApiaUtil } from '@apia/components';
import { formatMessage, getDateFormat } from '@apia/util';
import { validationsStore } from '@apia/validations';
import queriesQueue from '../../../../../store/querySlice/queueController';

export type TSavedDateFormat = {
  dateFrom: {
    fromStartOfValue: string;
    amountOf: number;
    fixedValue: string;
  };
  dateTo: { fixedValue: string };
  chkFromStartOf: boolean;
};

export type TFilterSaveInput = {
  id: string;
  value: string;
  type: 'text' | 'number';
  title: string;
  detectOnChange: boolean;
};
export type TFilterSaveSelect = {
  id: string;
  detectOnChange: boolean;
  value: string;
  options: TApiaFilterOption[];
  type: 'select';
  title: string;
};
export type TFilterSaveDate = {
  id: string;
  detectOnChange: boolean;
  filterToId?: string;
  dateFrom: {
    fromStartOfValue: string;
    amountOf: number;
    fixedValue: string;
  };
  dateTo?: {
    fixedValue: string;
  };
  chkFromStartOf: boolean;
  hideToFilter: boolean;
  title: string;
  type: 'date';
  hasChanged: boolean;
  dateToRequired?: boolean;
  dateFromRequired?: boolean;
};

export type TFilterSaveObject = (
  | TFilterSaveDate
  | TFilterSaveInput
  | TFilterSaveSelect
) & {
  filter: TApiaFilter;
  readonly: boolean;
  required: boolean;
};

export type TFilterSave = TFilterSaveObject[];

export default class FiltersSetterController {
  private static instance: FiltersSetterController;
  public fieldId: string = '';
  public filters: TFilterSave = [];
  public savedFilters: {
    name: string;
    id: string;
    selected: boolean;
    colFilId: {
      columnFilterId: string;
      columnFilterName: string;
      columnId: string;
    }[];
  }[] = [];
  public newFilterGroupName: string = '';
  public selectedFilterGroupName: string = '';
  public validDateRef: Record<string, boolean> = {};
  private constructor(outerFilters: TApiaFilter[], fieldId: string) {
    this.fieldId = fieldId;
    makeObservable(this, {
      filters: observable,
      savedFilters: observable,
      newFilterGroupName: observable,
      validDateRef: observable,
      selectedFilterGroupName: observable,
      updateFilters: action,
    });
    this.filters = outerFilters.map((innerFilter) =>
      this.createFilter(innerFilter, true),
    );
    this.loadSavedFilters();
  }
  public static getInstance(
    outerFilters?: TApiaFilter[],
    fieldId?: string,
  ): FiltersSetterController {
    if (!FiltersSetterController.instance) {
      FiltersSetterController.instance = new FiltersSetterController(
        outerFilters ?? [],
        fieldId ?? '',
      );
    }
    if (outerFilters) {
      this.instance.filters = outerFilters.map((innerFilter) =>
        this.instance.createFilter(innerFilter, true),
      );
    }
    if (fieldId) {
      this.instance.fieldId = fieldId;
    }
    return FiltersSetterController.instance;
  }
  private createFilter(
    innerFilter: TApiaFilter,
    fromNewInstance?: boolean,
  ): TFilterSaveObject {
    if (innerFilter.type === 'D' || innerFilter.type === 'date') {
      const savedValue = innerFilter.savedValue
        ? (JSON.parse(innerFilter.savedValue) as TSavedDateFormat)
        : '';
      const dateFormat = getDateFormat();
      if (savedValue) {
        const innerFilterCurrentValue = innerFilter.currentValue;
        const innerFilterToValue = innerFilter.filterToValue;
        const calculatedInitialDate = calculateDate({
          amountOf: savedValue.dateFrom.amountOf,
          chkFromStartOfValue: savedValue.chkFromStartOf,
          fixedValue: savedValue.dateFrom.fixedValue,
          fromStartOfValue: savedValue.dateFrom.fromStartOfValue,
        });
        let calculatedFinalDate = '';
        if (innerFilter.hideToFilter) {
          calculatedFinalDate = '';
        } else {
          if (savedValue.chkFromStartOf) {
            calculatedFinalDate = dayjs().startOf('day').format(dateFormat);
          } else if (savedValue.dateTo!.fixedValue) {
            calculatedFinalDate = savedValue.dateTo!.fixedValue;
          }
        }

        return {
          id: innerFilter.id,
          chkFromStartOf:
            innerFilterCurrentValue === calculatedInitialDate &&
            innerFilterToValue === calculatedFinalDate
              ? savedValue.chkFromStartOf
              : false,
          dateFrom:
            innerFilterCurrentValue === calculatedInitialDate &&
            innerFilterToValue === calculatedFinalDate
              ? savedValue.dateFrom
              : {
                  ...savedValue.dateFrom,
                  fixedValue: innerFilterCurrentValue,
                },
          dateTo: innerFilter.hideToFilter
            ? undefined
            : innerFilterCurrentValue === calculatedInitialDate &&
                innerFilterToValue === calculatedFinalDate
              ? savedValue.dateTo
              : { ...savedValue.dateTo, fixedValue: innerFilterToValue },
          filterToId: innerFilter.filterToId,
          hideToFilter: innerFilter.hideToFilter,
          title: innerFilter.title,
          type: 'date',
          hasChanged: false,
          detectOnChange: innerFilter.detectOnChange,
          filter: { ...innerFilter, savedValue: innerFilter.savedValue },
          readonly: !!innerFilter.readonly,
          required: !!innerFilter.required,
          dateFromRequired: innerFilter.dateFromRequired,
          dateToRequired: innerFilter.dateToRequired,
        } as TFilterSaveDate & {
          filter: TApiaFilter;
          readonly: boolean;
          required: boolean;
        };
      }
      const realFilter = fromNewInstance
        ? undefined
        : this.filters.find((f) => f.filter.id === innerFilter.id);
      return {
        id: innerFilter.id,
        dateFrom:
          realFilter?.type === 'date'
            ? realFilter.dateFrom
            : {
                amountOf: 0,
                fromStartOfValue: 'actualDay',
                fixedValue: innerFilter.currentValue,
              },
        dateTo: innerFilter.hideToFilter
          ? undefined
          : realFilter?.type === 'date'
            ? realFilter.dateTo
            : { fixedValue: innerFilter.filterToValue },
        filterToId: innerFilter.filterToId,
        chkFromStartOf:
          realFilter?.type === 'date' ? realFilter.chkFromStartOf : false,
        hideToFilter: innerFilter.hideToFilter,
        title: innerFilter.title,
        type: 'date',
        hasChanged: false,
        detectOnChange: innerFilter.detectOnChange,
        filter: { ...innerFilter, savedValue: innerFilter.savedValue },
        readonly: !!innerFilter.readonly,
        required: !!innerFilter.required,
        dateFromRequired: innerFilter.dateFromRequired,
        dateToRequired: innerFilter.dateToRequired,
      } as TFilterSaveDate & {
        filter: TApiaFilter;
        readonly: boolean;
        required: boolean;
      };
    } else if (innerFilter.options) {
      return {
        id: innerFilter.id,
        options: innerFilter.options,
        value: innerFilter.currentValue,
        type: 'select',
        title: innerFilter.title,
        detectOnChange: innerFilter.detectOnChange,
        filter: { ...innerFilter, savedValue: innerFilter.savedValue },
        readonly: !!innerFilter.readonly,
        required: !!innerFilter.required,
      } as TFilterSaveSelect & {
        filter: TApiaFilter;
        readonly: boolean;
        required: boolean;
      };
    } else {
      return {
        id: innerFilter.id,
        value: innerFilter.currentValue,
        type:
          innerFilter.type === 'N' || innerFilter.type === 'number'
            ? 'number'
            : 'text',
        title: innerFilter.title,
        detectOnChange: innerFilter.detectOnChange,
        filter: { ...innerFilter, savedValue: innerFilter.savedValue },
        readonly: !!innerFilter.readonly,
        required: !!innerFilter.required,
      } as TFilterSaveInput & {
        filter: TApiaFilter;
        readonly: boolean;
        required: boolean;
      };
    }
  }
  private getUpdatedFilter(innerFilter: TApiaFilter): TFilterSaveObject {
    const newFilter = this.createFilter(innerFilter);
    const oldFilter = this.filters.find((f) => f.id === innerFilter.id);
    if (oldFilter) {
      if (oldFilter.type === newFilter.type) {
        if (oldFilter.type === 'date') {
          const dateFormat = getDateFormat();
          const newDateFilter = newFilter as TFilterSaveDate & {
            filter: TApiaFilter;
            readonly: boolean;
            required: boolean;
          };
          const oldDateFilter = oldFilter as TFilterSaveDate & {
            filter: TApiaFilter;
            readonly: boolean;
            required: boolean;
          };
          if (newDateFilter.hideToFilter && oldDateFilter.hideToFilter) {
            const newDateValue = calculateDate({
              amountOf: newDateFilter.dateFrom.amountOf,
              chkFromStartOfValue: newDateFilter.chkFromStartOf,
              fixedValue: newDateFilter.dateFrom.fixedValue,
              fromStartOfValue: newDateFilter.dateFrom.fromStartOfValue,
            });
            const oldDateValue = calculateDate({
              amountOf: oldDateFilter.dateFrom.amountOf,
              chkFromStartOfValue: oldDateFilter.chkFromStartOf,
              fixedValue: oldDateFilter.dateFrom.fixedValue,
              fromStartOfValue: oldDateFilter.dateFrom.fromStartOfValue,
            });
            if (newDateValue === oldDateValue) {
              return {
                ...newDateFilter,
                dateFrom: newDateFilter.dateFrom ?? oldDateFilter.dateFrom,
                chkFromStartOf:
                  newDateFilter.chkFromStartOf ?? oldDateFilter.chkFromStartOf,
              } as TFilterSaveObject;
            }
          } else if (
            !newDateFilter.hideToFilter &&
            !oldDateFilter.hideToFilter
          ) {
            let newDateFrom = '';
            if (newDateFilter.chkFromStartOf) {
              newDateFrom = calculateDate({
                amountOf: newDateFilter.dateFrom.amountOf,
                chkFromStartOfValue: newDateFilter.chkFromStartOf,
                fromStartOfValue: newDateFilter.dateFrom.fromStartOfValue,
                fixedValue: newDateFilter.dateFrom.fixedValue,
              });
            } else if (newDateFilter.dateFrom.fixedValue) {
              newDateFrom = newDateFilter.dateFrom.fixedValue;
            }
            let newDateTo = '';
            if (newDateFilter.chkFromStartOf) {
              newDateTo = dayjs().startOf('day').format(dateFormat);
            } else if (newDateFilter.dateTo!.fixedValue) {
              newDateTo = newDateFilter.dateTo!.fixedValue;
            }

            let oldDateFrom = '';
            if (newDateFilter.chkFromStartOf) {
              oldDateFrom = calculateDate({
                amountOf: newDateFilter.dateFrom.amountOf,
                chkFromStartOfValue: newDateFilter.chkFromStartOf,
                fromStartOfValue: newDateFilter.dateFrom.fromStartOfValue,
                fixedValue: newDateFilter.dateFrom.fixedValue,
              });
            } else if (newDateFilter.dateFrom.fixedValue) {
              oldDateFrom = newDateFilter.dateFrom.fixedValue;
            }
            let oldDateTo = '';
            if (newDateFilter.chkFromStartOf) {
              oldDateTo = dayjs().startOf('day').format(dateFormat);
            } else if (newDateFilter.dateTo!.fixedValue) {
              oldDateTo = newDateFilter.dateTo!.fixedValue;
            }

            if (newDateFrom === oldDateFrom && newDateTo === oldDateTo) {
              return {
                ...newDateFilter,
                dateFrom: newDateFilter.dateFrom ?? oldDateFilter.dateFrom,
                chkFromStartOf:
                  newDateFilter.chkFromStartOf ?? oldDateFilter.chkFromStartOf,
                dateTo: newDateFilter.dateTo ?? oldDateFilter.dateTo,
              } as TFilterSaveObject;
            }
          }
        }
      }
    }
    return {
      ...newFilter,
    };
  }
  updateFilters = (newFilters: TApiaFilter[]) => {
    runInAction(() => {
      this.filters = this.filters.map((existingFilter) => {
        const newFilter = newFilters.find((f) => f.id === existingFilter.id);
        return newFilter ? this.getUpdatedFilter(newFilter) : existingFilter;
      });
    });
  };
  getSettedFilter = async (filterId: string) => {
    await store.dispatch(
      queryActions.loadSavedFilters(
        this.fieldId === 'userQuery'
          ? {
              fieldId: this.fieldId,
              query: { filterGroupId: filterId },
            }
          : {
              fieldId: this.fieldId,
              query: {
                filterGroupId: filterId,
                workingMode:
                  this.fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
              },
            },
      ),
    );
    const filters = store.getState().querySlice.queries[this.fieldId].filters;

    if (filters) {
      const apiaFilters = filters?.map((f) => {
        return f.apiaFilter;
      });

      this.updateFilters(apiaFilters);
    }
  };
  loadSavedFilters = async () => {
    const response = await ApiaApi.get<UserQryFilters>(
      makeApiaUrl(
        this.fieldId === 'userQuery'
          ? {
              action: 'getAllUserQryFilters',
            }
          : {
              action: 'getAllUserQryFilters',
              workingMode:
                this.fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
            },
      ),
    );

    if (response?.data && response.data.userQryFilter) {
      const userQrySavedFilter = arrayOrArray(response.data.userQryFilter);
      const savedFilters: FiltersSetterController['savedFilters'] =
        userQrySavedFilter.map((savedFilter) => {
          return {
            id: savedFilter.id,
            name: savedFilter.name,
            selected: false,
            colFilId: arrayOrArray(
              savedFilter.userQryColumnFilters.userQryColumnFilter,
            ).map((cf) => {
              return {
                columnFilterId: cf.columnFilterId,
                columnFilterName: cf.columnFilterName,
                columnId: cf.columnId,
              };
            }),
          } as {
            name: string;
            id: string;
            selected: boolean;
            colFilId: {
              columnFilterId: string;
              columnFilterName: string;
              columnId: string;
            }[];
          };
        });

      this.savedFilters = savedFilters;
    }
  };
  emptyFiltersValues = () => {
    this.filters = this.filters.map((f) => {
      if (f.type === 'date') {
        if (f.hideToFilter) {
          return {
            ...f,
            chkFromStartOf: false,
            dateFrom: {
              fromStartOfValue: 'actualDay',
              amountOf: 0,
              fixedValue: '',
            },
          };
        }
        return {
          ...f,
          chkFromStartOf: false,
          dateFrom: {
            fromStartOfValue: 'actualDay',
            amountOf: 0,
            fixedValue: '',
          },
          dateTo: { fixedValue: '' },
        };
      } else if (f.type == 'select') {
        return {
          ...f,
          value: '',
          options: f.options.map((o) => {
            return { ...o, selected: false };
          }),
        };
      } else {
        return { ...f, value: '' };
      }
    });
  };
  deleteSavedFilter = async () => {
    const selectedOption = this.savedFilters.find((saved) => {
      return saved.selected;
    });
    this.emptyFiltersValues();
    await store.dispatch(
      queryActions.deleteFilters(
        this.fieldId === 'userQuery'
          ? { listId: this.fieldId }
          : {
              listId: this.fieldId,
              workingMode:
                this.fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
            },
      ),
    );

    await ApiaApi.post(
      makeApiaUrl(
        this.fieldId === 'userQuery'
          ? {
              action: 'deleteUserQryFilters',
              filterIdsToDelete: selectedOption?.id,
            }
          : {
              action: 'deleteUserQryFilters',
              filterIdsToDelete: selectedOption?.id,
              workingMode:
                this.fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
            },
      ),
    ).finally(async () => {
      await store.dispatch(
        queryActions.updateData(
          this.fieldId === 'userQuery'
            ? {
                fieldId: this.fieldId,
                forceRefreshFromSavedFilters: true,
              }
            : {
                fieldId: this.fieldId,
                forceRefreshFromSavedFilters: true,
                queryData: {
                  workingMode:
                    this.fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
                },
              },
        ),
      );
    });
    this.savedFilters = this.savedFilters.filter(
      (saved) => saved.id !== selectedOption?.id,
    );
  };

  async validate(type: string) {
    const dateFormat = getDateFormat();
    const filters = store.getState().responsiveTableSlice[this.fieldId].filters;

    /**this statement is to verify if there are required filters and act accordingly */
    if (
      filters &&
      filters.some((_, i) => {
        const controllerValues = this.filters[i];
        const filterType = controllerValues.type;
        if (filterType === 'date') {
          if (controllerValues.hasChanged) {
            if (controllerValues.hideToFilter) {
              const date = calculateDate({
                amountOf: controllerValues.dateFrom.amountOf,
                chkFromStartOfValue: controllerValues.chkFromStartOf,
                fromStartOfValue: controllerValues.dateFrom.fromStartOfValue,
                fixedValue: controllerValues.dateFrom.fixedValue,
              });
              return (
                (date === '' || date === null || date === undefined) &&
                controllerValues.required
              );
            } else {
              let dateFrom = '';
              if (controllerValues.chkFromStartOf) {
                dateFrom = calculateDate({
                  amountOf: controllerValues.dateFrom.amountOf,
                  chkFromStartOfValue: controllerValues.chkFromStartOf,
                  fromStartOfValue: controllerValues.dateFrom.fromStartOfValue,
                  fixedValue: controllerValues.dateFrom.fixedValue,
                });
              } else if (controllerValues.dateFrom.fixedValue) {
                dateFrom = controllerValues.dateFrom.fixedValue;
              }
              let dateTo = '';
              if (controllerValues.chkFromStartOf) {
                dateTo = dayjs().startOf('day').format(dateFormat);
              } else if (controllerValues.dateTo!.fixedValue) {
                dateTo = controllerValues.dateTo!.fixedValue;
              }

              return (
                (dateFrom === '' ||
                  dateFrom === null ||
                  dateFrom === undefined) &&
                (dateTo === '' || dateTo === null || dateTo === undefined) &&
                controllerValues.required
              );
            }
          } else {
            if (controllerValues.hideToFilter) {
              const date = calculateDate({
                amountOf: controllerValues.dateFrom.amountOf,
                chkFromStartOfValue: controllerValues.chkFromStartOf,
                fromStartOfValue: controllerValues.dateFrom.fromStartOfValue,
                fixedValue: controllerValues.dateFrom.fixedValue,
              });
              return (
                (date === '' || date === null || date === undefined) &&
                controllerValues.required
              );
            } else {
              let dateFrom = '';
              if (controllerValues.dateFrom.fixedValue) {
                dateFrom = controllerValues.dateFrom.fixedValue;
              } else if (controllerValues.chkFromStartOf) {
                dateFrom = calculateDate({
                  amountOf: controllerValues.dateFrom.amountOf,
                  chkFromStartOfValue: controllerValues.chkFromStartOf,
                  fromStartOfValue: controllerValues.dateFrom.fromStartOfValue,
                  fixedValue: controllerValues.dateFrom.fixedValue,
                });
              }
              let dateTo = '';
              if (controllerValues.dateTo!.fixedValue) {
                dateTo = controllerValues.dateTo!.fixedValue;
              } else if (controllerValues.chkFromStartOf) {
                dateTo = dayjs().startOf('day').format(dateFormat);
              }

              return (
                (dateFrom === '' ||
                  dateFrom === null ||
                  dateFrom === undefined) &&
                (dateTo === '' || dateTo === null || dateTo === undefined) &&
                controllerValues.required
              );
            }
          }
        } else {
          return (
            (controllerValues.value === '' ||
              controllerValues.value === null ||
              controllerValues.value === undefined) &&
            controllerValues.required
          );
        }
      })
    ) {
      // Helper function to check if a value is empty (undefined or empty string)
      const isEmpty = (value: any) => value === undefined || value === '';

      const emptyFilters: string[] = [];
      // La siguiente logica se encarga de contar cuantos filtros requeridos hay, y luego cuantos tienen valor
      filters.forEach((innerFilter) => {
        if (innerFilter.required) {
          if (['date', 'D'].includes(innerFilter.type as string)) {
            const apiaFilter = innerFilter;
            if (
              (apiaFilter.dateFromRequired ||
                apiaFilter.dateRequired ||
                apiaFilter.dateToRequired) &&
              (isEmpty(apiaFilter.currentValue) ||
                isEmpty(apiaFilter.filterToValue))
            ) {
              emptyFilters.push(
                (innerFilter.title ?? innerFilter.id) as string,
              );
              if (apiaFilter.dateRequired) {
                validationsStore.updateFieldStore(
                  'FiltersSetter',
                  apiaFilter.id + '_fixedStart',
                  { error: window.MSG_REQ_FIELD },
                );
                validationsStore.updateFieldStore(
                  'FiltersSetter',
                  apiaFilter.id + '_fixedEnd',
                  { error: window.MSG_REQ_FIELD },
                );
              } else if (apiaFilter.dateFromRequired) {
                validationsStore.updateFieldStore(
                  'FiltersSetter',
                  apiaFilter.id + '_fixedStart',
                  { error: window.MSG_REQ_FIELD },
                );
              } else if (apiaFilter.dateToRequired) {
                validationsStore.updateFieldStore(
                  'FiltersSetter',
                  apiaFilter.id + '_fixedEnd',
                  { error: window.MSG_REQ_FIELD },
                );
              }
            }
          } else if (!innerFilter.currentValue) {
            emptyFilters.push((innerFilter.title ?? innerFilter.id) as string);

            validationsStore.updateFieldStore(
              'FiltersSetter',
              String(innerFilter.id),
              { error: window.MSG_REQ_FIELD },
            );
          }
        }
      });
      ApiaUtil.instance.notifications.notify({
        message: formatMessage(window.LBL_FILTER_REQUIRED_LIST_QUERY, {
          TOK1: emptyFilters.join(', '),
        }),
      });
      return false;
    }

    /* La siguiente logica se encarga de verificar si en un filtro de fecha de tipo dinamico se colocó una cantidad de tiempo negativa */
    if (
      this.filters.some((f) => {
        if (
          f.type === 'date' &&
          f.chkFromStartOf &&
          [
            'daysAmount',
            'weeksAmount',
            'monthsAmount',
            'quartersAmount',
            'semestersAmount',
            'yearsAmount',
          ].includes(f.dateFrom.fromStartOfValue) &&
          f.dateFrom.amountOf < 0
        ) {
          return true;
        } else {
          return false;
        }
      })
    ) {
      ApiaUtil.instance.notifications.notify({
        message: getLabel('lblErrNoNegativeDateValues').text,
        //'No se pueden colocar cantidades negativas de tiempo en los campos dinamicos de fecha.',
      });

      this.filters.forEach((f) => {
        if (
          f.type === 'date' &&
          f.chkFromStartOf &&
          [
            'daysAmount',
            'weeksAmount',
            'monthsAmount',
            'quartersAmount',
            'semestersAmount',
            'yearsAmount',
          ].includes(f.dateFrom.fromStartOfValue) &&
          f.dateFrom.amountOf < 0
        ) {
          validationsStore.updateFieldStore(
            'FiltersSetter',
            f.id + '_amountOf',
            { error: getLabel('lblErrNoNegativeDateValues').text },
          );
        }
      });
      return false;
    }

    /** logica para verificar si los campos de fecha tienen fechas validas */
    if (
      this.filters.some((f) => {
        if (
          f.type === 'date' &&
          !f.hideToFilter &&
          !f.chkFromStartOf &&
          f.dateFrom.fixedValue !== '' &&
          f.dateTo?.fixedValue !== ''
        ) {
          const dateFromValue = dayjs(
            `${f.dateFrom.fixedValue}`,
            `${dateFormat}`,
          );
          const dateToValue = dayjs(`${f.dateTo?.fixedValue}`, `${dateFormat}`);
          if (dateFromValue.isAfter(dateToValue)) {
            return true;
          }
          return false;
        } else {
          return false;
        }
      })
    ) {
      ApiaUtil.instance.notifications.notify({
        message: window.MSG_FEC_FIN_MAY_FEC_INI,
        //'No se pueden colocar cantidades negativas de tiempo en los campos dinamicos de fecha.',
      });
      this.filters.forEach((f) => {
        if (
          f.type === 'date' &&
          !f.hideToFilter &&
          !f.chkFromStartOf &&
          f.dateFrom.fixedValue !== '' &&
          f.dateTo?.fixedValue !== ''
        ) {
          const dateFromValue = dayjs(
            `${f.dateFrom.fixedValue}`,
            `${dateFormat}`,
          );
          const dateToValue = dayjs(`${f.dateTo?.fixedValue}`, `${dateFormat}`);
          if (dateFromValue.isAfter(dateToValue)) {
            validationsStore.updateFieldStore(
              'FiltersSetter',
              f.id + '_fixedStart',
              { error: window.MSG_FEC_FIN_MAY_FEC_INI },
            );
            validationsStore.updateFieldStore(
              'FiltersSetter',
              f.id + '_fixedEnd',
              { error: window.MSG_FEC_FIN_MAY_FEC_INI },
            );
          }
        }
      });
      return false;
    }
    // const validationRes = await validationsStore.validateForm('FiltersSetter');
    // if (!hasSucceedFormValidation(validationRes)) {
    //   return false;
    // }
    queriesQueue.runAction(
      'directUpdateFilters',
      async () => {
        await store.dispatch(
          queryActions.updateFilters(
            this.fieldId === 'userQuery'
              ? {
                  fieldId: this.fieldId,
                  fromFiltersSetters: true,
                  filters: filters.map((fil, i) => {
                    const controllerValues = this.filters[i];
                    const filterType = controllerValues.type;
                    if (filterType === 'date') {
                      if (controllerValues.hasChanged) {
                        if (controllerValues.hideToFilter) {
                          const date = calculateDate({
                            amountOf: controllerValues.dateFrom.amountOf,
                            chkFromStartOfValue:
                              controllerValues.chkFromStartOf,
                            fromStartOfValue:
                              controllerValues.dateFrom.fromStartOfValue,
                            fixedValue: controllerValues.dateFrom.fixedValue,
                          });
                          return {
                            ...fil,
                            currentValue: date,
                            changeFiltersTimestamp: Date.now(),
                          };
                        } else {
                          let dateFrom = '';
                          if (controllerValues.chkFromStartOf) {
                            dateFrom = calculateDate({
                              amountOf: controllerValues.dateFrom.amountOf,
                              chkFromStartOfValue:
                                controllerValues.chkFromStartOf,
                              fromStartOfValue:
                                controllerValues.dateFrom.fromStartOfValue,
                              fixedValue: controllerValues.dateFrom.fixedValue,
                            });
                          } else if (controllerValues.dateFrom.fixedValue) {
                            dateFrom = controllerValues.dateFrom.fixedValue;
                          }
                          let dateTo = '';
                          if (controllerValues.chkFromStartOf) {
                            dateTo = dayjs().startOf('day').format(dateFormat);
                          } else if (controllerValues.dateTo!.fixedValue) {
                            dateTo = controllerValues.dateTo!.fixedValue;
                          }

                          return {
                            ...fil,
                            currentValue: dateFrom,
                            filterToValue: dateTo,
                            changeFiltersTimestamp: Date.now(),
                          };
                        }
                      } else {
                        return fil;
                      }
                    } else {
                      return {
                        ...fil,
                        currentValue: controllerValues.value,
                        changeFiltersTimestamp: Date.now(),
                      };
                    }
                  }),
                  forceSearch: false, // no se quiere que busque al confirmar el modal
                  avoidEventDetectOnChange: true,
                  forceRefreshFromSavedFilters: true,
                }
              : {
                  fieldId: this.fieldId,
                  fromFiltersSetters: true,
                  filters: filters.map((fil, i) => {
                    const controllerValues = this.filters[i];
                    const filterType = controllerValues.type;
                    if (filterType === 'date') {
                      if (controllerValues.hasChanged) {
                        if (controllerValues.hideToFilter) {
                          const date = calculateDate({
                            amountOf: controllerValues.dateFrom.amountOf,
                            chkFromStartOfValue:
                              controllerValues.chkFromStartOf,
                            fromStartOfValue:
                              controllerValues.dateFrom.fromStartOfValue,
                            fixedValue: controllerValues.dateFrom.fixedValue,
                          });
                          return {
                            ...fil,
                            currentValue: date,
                            changeFiltersTimestamp: Date.now(),
                          };
                        } else {
                          let dateFrom = '';
                          if (controllerValues.chkFromStartOf) {
                            dateFrom = calculateDate({
                              amountOf: controllerValues.dateFrom.amountOf,
                              chkFromStartOfValue:
                                controllerValues.chkFromStartOf,
                              fromStartOfValue:
                                controllerValues.dateFrom.fromStartOfValue,
                              fixedValue: controllerValues.dateFrom.fixedValue,
                            });
                          } else if (controllerValues.dateFrom.fixedValue) {
                            dateFrom = controllerValues.dateFrom.fixedValue;
                          }
                          let dateTo = '';
                          if (controllerValues.chkFromStartOf) {
                            dateTo = dayjs().startOf('day').format(dateFormat);
                          } else if (controllerValues.dateTo!.fixedValue) {
                            dateTo = controllerValues.dateTo!.fixedValue;
                          }

                          return {
                            ...fil,
                            currentValue: dateFrom,
                            filterToValue: dateTo,
                            changeFiltersTimestamp: Date.now(),
                          };
                        }
                      } else {
                        return fil;
                      }
                    } else {
                      return {
                        ...fil,
                        currentValue: controllerValues.value,
                        changeFiltersTimestamp: Date.now(),
                      };
                    }
                  }),
                  forceSearch: false, // no se quiere que busque al confirmar el modal
                  avoidEventDetectOnChange: true,
                  forceRefreshFromSavedFilters: true,
                  query: {
                    workingMode:
                      this.fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
                  },
                },
          ),
        );
      },
      ['fromUpdateFilters', 'fromSearchfromUpdateFilters'],
    );
    if (type === 'create') {
      if (this.newFilterGroupName === '') {
        ApiaUtil.instance.notifications.notify({
          message: getLabel('lblErrFilterNameRequired').text, //'El filtrado debe tener un nombre',
        });
        return false;
      }
      const valueMap = new Map(
        this.filters.map((item) => {
          if (item.type === 'date') {
            if (item.hideToFilter) {
              const fixedDate =
                item.dateFrom.fixedValue !== ''
                  ? dayjs(item.dateFrom.fixedValue, getDateFormat(), true)
                  : '';
              let fixedDateTimestamp: string = '';
              if (typeof fixedDate !== 'string' && fixedDate.isValid()) {
                fixedDateTimestamp = fixedDate.valueOf().toString();
              }
              return [
                item.id,
                JSON.stringify({
                  dateFrom: { ...item.dateFrom, fixedDate: fixedDateTimestamp },
                  chkFromStartOf: item.chkFromStartOf,
                }),
              ];
            }
            const fixedFromDate =
              item.dateFrom.fixedValue !== ''
                ? dayjs(item.dateFrom.fixedValue, getDateFormat(), true)
                : '';
            let fixedFromDateTimestamp: string = '';
            if (typeof fixedFromDate !== 'string' && fixedFromDate.isValid()) {
              fixedFromDateTimestamp = fixedFromDate.valueOf().toString();
            }
            const fixedToDate =
              item.dateTo!.fixedValue !== ''
                ? dayjs(item.dateTo!.fixedValue, getDateFormat(), true)
                : '';
            let fixedToDateTimestamp: string = '';
            if (typeof fixedToDate !== 'string' && fixedToDate.isValid()) {
              fixedToDateTimestamp = fixedToDate.valueOf().toString();
            }
            return [
              item.id,
              JSON.stringify({
                dateFrom: {
                  ...item.dateFrom,
                  fixedValue: fixedFromDateTimestamp,
                },
                dateTo: { fixedValue: fixedToDateTimestamp },
                chkFromStartOf: item.chkFromStartOf,
              }),
            ];
          }
          return [item.id, item.value];
        }),
      );

      const result = filters.reduce(
        (acc, item) => {
          const value = valueMap.get(item.id as string);
          if (value !== undefined && value !== null) {
            acc[`columnName${item.column as string}`] = value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );
      this.savedFilters = this.savedFilters.map((sf) => {
        return { ...sf, selected: false };
      });
      let noErrors = true;
      const saveFilters = async () => {
        const saveResult = await ApiaApi.post(
          makeApiaUrl({
            action: 'createUserQryFilters',
          }),
          this.fieldId === 'userQuery'
            ? {
                postData: { name: this.newFilterGroupName, ...result },
                postDataTreatement: 'stringify',
              }
            : {
                postData: {
                  name: this.newFilterGroupName,
                  ...result,
                  workingMode:
                    this.fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
                },
                postDataTreatement: 'stringify',
              },
        );
        if (saveResult && saveResult.data?.sysMessages !== undefined) {
          noErrors = false;
        }
      };
      await saveFilters().then(async () => {
        if (noErrors) {
          await this.loadSavedFilters();
          this.savedFilters = this.savedFilters.map((sf) => {
            if (sf.name === this.newFilterGroupName) {
              return { ...sf, selected: true };
            } else {
              return sf;
            }
          });
        }
      });
      return noErrors;
    } else {
      if (!this.savedFilters.some((saved) => saved.selected)) {
        ApiaUtil.instance.notifications.notify({
          message: getLabel('lblErrMustSelectFilter').text, //'Debe seleccionar un filtrado',
        });
        return false;
      }
      const valueMap = new Map(
        this.filters.map((item) => {
          if (item.type === 'date') {
            if (item.hideToFilter) {
              const fixedDate =
                item.dateFrom.fixedValue !== ''
                  ? dayjs(item.dateFrom.fixedValue, getDateFormat(), true)
                  : '';
              let fixedDateTimestamp: string = '';
              if (typeof fixedDate !== 'string' && fixedDate.isValid()) {
                fixedDateTimestamp = fixedDate.valueOf().toString();
              }
              return [
                item.id,
                JSON.stringify({
                  dateFrom: { ...item.dateFrom, fixedDate: fixedDateTimestamp },
                  chkFromStartOf: item.chkFromStartOf,
                }),
              ];
            }
            const fixedFromDate =
              item.dateFrom.fixedValue !== ''
                ? dayjs(item.dateFrom.fixedValue, getDateFormat(), true)
                : '';
            let fixedFromDateTimestamp: string = '';
            if (typeof fixedFromDate !== 'string' && fixedFromDate.isValid()) {
              fixedFromDateTimestamp = fixedFromDate.valueOf().toString();
            }
            const fixedToDate =
              item.dateTo!.fixedValue !== ''
                ? dayjs(item.dateTo!.fixedValue, getDateFormat(), true)
                : '';
            let fixedToDateTimestamp: string = '';
            if (typeof fixedToDate !== 'string' && fixedToDate.isValid()) {
              fixedToDateTimestamp = fixedToDate.valueOf().toString();
            }
            return [
              item.id,
              JSON.stringify({
                dateFrom: {
                  ...item.dateFrom,
                  fixedValue: fixedFromDateTimestamp,
                },
                dateTo: { fixedValue: fixedToDateTimestamp },
                chkFromStartOf: item.chkFromStartOf,
              }),
            ];
          }
          return [item.id, item.value];
        }),
      );

      const selectedSavedFilter = this.savedFilters.find(
        (saved) => saved.selected,
      );
      const result = filters.reduce(
        (acc, item) => {
          const value = valueMap.get(item.id as string);
          const controllerFilterData = selectedSavedFilter?.colFilId.find(
            (cf) => {
              return cf.columnFilterName === item.column;
            },
          );
          if (value !== undefined && value !== null && controllerFilterData) {
            acc[
              `columnId${controllerFilterData.columnId as string}_colFilterId${controllerFilterData.columnFilterId as string}`
            ] = value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );
      const saveFilters = async () => {
        await ApiaApi.post(
          makeApiaUrl({
            action: 'updateUserQryFilters',
          }),
          this.fieldId === 'userQuery'
            ? {
                postData: {
                  name: selectedSavedFilter?.name,
                  id: selectedSavedFilter?.id,
                  ...result,
                },
                postDataTreatement: 'stringify',
              }
            : {
                postData: {
                  name: selectedSavedFilter?.name,
                  id: selectedSavedFilter?.id,
                  ...result,
                  workingMode:
                    this.fieldId === 'taskListQueryFreeTasks' ? 'R' : 'I',
                },
                postDataTreatement: 'stringify',
              },
        );
      };
      await saveFilters().then(async () => {
        this.savedFilters = this.savedFilters.map((sf) => {
          if (sf.name === this.selectedFilterGroupName) {
            return { ...sf, selected: true };
          } else {
            return sf;
          }
        });
      });
      return true;
    }
  }
}
